import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Wrench, Paintbrush, Settings, CheckCircle, Phone, ArrowRight } from 'lucide-react';
import { useSiteSettings } from '../context/SiteSettingsContext';
import Lightbox from '../components/Lightbox';
import SEO from '../components/SEO';

const Home = () => {
  const { settings, loading, getSetting } = useSiteSettings();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const services = [
    {
      icon: <Wrench size={32} color="#1a1a1a" />,
      title: getSetting('home_service1_title', 'Réparation'),
      description: getSetting('home_service1_desc', 'Réparation mécanique et hydraulique de tous types d\'engins de chantier.'),
      link: '/reparation'
    },
    {
      icon: <Paintbrush size={32} color="#1a1a1a" />,
      title: getSetting('home_service2_title', 'Peinture'),
      description: getSetting('home_service2_desc', 'Remise en peinture complète ou partielle de vos engins.'),
      link: '/peinture'
    },
    {
      icon: <Settings size={32} color="#1a1a1a" />,
      title: getSetting('home_service3_title', 'Entretien'),
      description: getSetting('home_service3_desc', 'Entretien préventif et contrats de maintenance.'),
      link: '/entretien'
    }
  ];

  const reasons = [
    { title: getSetting('home_reason1_title', 'Expérience'), text: getSetting('home_reason1_text', 'Plus de 15 ans d\'expertise') },
    { title: getSetting('home_reason2_title', 'Rapidité'), text: getSetting('home_reason2_text', 'Interventions rapides') },
    { title: getSetting('home_reason3_title', 'Toutes marques'), text: getSetting('home_reason3_text', 'LiuGong, Komatsu, Caterpillar...') },
    { title: getSetting('home_reason4_title', 'Qualité'), text: getSetting('home_reason4_text', 'Pièces d\'origine') }
  ];

  const galleryImages = [
    { src: getSetting('home_gallery_image1', 'https://images.pexels.com/photos/2058729/pexels-photo-2058729.jpeg?auto=compress&cs=tinysrgb&w=800'), alt: 'Engin 1', caption: 'Pelle hydraulique' },
    { src: getSetting('home_gallery_image2', 'https://images.unsplash.com/photo-1664312616511-81fe2e745cb3?auto=format&fit=crop&w=800&q=80'), alt: 'Engin 2', caption: 'Excavatrice' },
    { src: getSetting('home_gallery_image3', 'https://images.pexels.com/photos/5125783/pexels-photo-5125783.jpeg?auto=compress&cs=tinysrgb&w=800'), alt: 'Engin 3', caption: 'Tracto-pelle' },
    { src: getSetting('home_gallery_image4', 'https://images.unsplash.com/photo-1652922660696-60c68ec51582?auto=format&fit=crop&w=800&q=80'), alt: 'Engin 4', caption: 'Chargeuse' }
  ];

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const heroImage = getSetting('home_hero_image', 'https://images.pexels.com/photos/2058729/pexels-photo-2058729.jpeg?auto=compress&cs=tinysrgb&w=1920');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#F5C500] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="home-page">
      <SEO
        title="Réparation & Peinture Engins de Chantier à La Réunion"
        description="Expert en réparation mécanique, peinture et entretien d'engins de chantier à La Réunion. Pelles hydrauliques, mini-pelles, chargeuses — toutes marques : LiuGong, Komatsu, Caterpillar, Kubota. Devis gratuit. Intervention sur toute l'île."
        canonical="/"
        image="https://images.pexels.com/photos/2058729/pexels-photo-2058729.jpeg?auto=compress&cs=tinysrgb&w=1200"
      />
      {/* Hero Section */}
      <section 
        className="hero-section" 
        style={{ backgroundImage: `url(${heroImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl animate-slide-up">
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              {getSetting('home_hero_title', 'Votre Expert en Réparation & Peinture d\'Engins')}
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              {getSetting('home_hero_subtitle', 'À La Réunion, nous réparons et remettons en état vos engins de chantier.')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="btn-primary text-center" data-testid="hero-cta">
                {getSetting('home_hero_cta', 'Demander un Devis Gratuit')}
              </Link>
              <a 
                href={`tel:${getSetting('phone', '0693526449')}`}
                className="btn-secondary flex items-center justify-center gap-2"
                data-testid="hero-phone"
              >
                <Phone size={20} />
                {getSetting('phone', '06 93 52 64 49').replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5')}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-white py-20 md:py-32" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span 
              className="text-sm font-bold tracking-widest uppercase text-[#F5C500] block mb-4"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Nos Services
            </span>
            <h2 
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1a1a1a] mb-6"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              {getSetting('home_services_title', 'Des Solutions Complètes pour Vos Engins')}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              {getSetting('home_services_subtitle', 'De la réparation mécanique à la remise en peinture.')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={index} 
                className="service-card bg-white shadow-lg p-8 flex flex-col h-full"
                data-testid={`service-card-${index}`}
              >
                <div className="icon-container mb-6">
                  {service.icon}
                </div>
                <h3 
                  className="text-2xl font-bold mb-4 text-[#1a1a1a]"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-6 flex-grow">{service.description}</p>
                <Link 
                  to={service.link} 
                  className="inline-flex items-center gap-2 text-[#1a1a1a] font-semibold hover:text-[#F5C500] transition-colors group"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}
                >
                  En savoir plus 
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section-gray py-20 md:py-32" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span 
                className="text-sm font-bold tracking-widest uppercase text-[#F5C500] block mb-4"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Pourquoi Nous Choisir
              </span>
              <h2 
                className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1a1a1a] mb-8"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                {getSetting('home_why_title', 'L\'Expertise au Service de Vos Engins')}
              </h2>
              <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                {getSetting('home_why_subtitle', 'Basés à Saint-Leu, nous intervenons sur toute l\'île de La Réunion.')}
              </p>
              <div className="space-y-4">
                {reasons.map((reason, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <CheckCircle size={24} className="text-[#F5C500] flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-[#1a1a1a]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                        {reason.title}
                      </h4>
                      <p className="text-gray-600">{reason.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img 
                src={getSetting('home_why_image', 'https://images.pexels.com/photos/2058729/pexels-photo-2058729.jpeg?auto=compress&cs=tinysrgb&w=800')}
                alt="Engin de chantier"
                className="w-full h-auto shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#F5C500] p-6 shadow-xl">
                <span 
                  className="text-4xl font-bold text-[#1a1a1a] block"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  {getSetting('years_experience', '15+')}
                </span>
                <span className="text-[#1a1a1a] font-medium">Années d'expérience</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="section-white py-20 md:py-32" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span 
              className="text-sm font-bold tracking-widest uppercase text-[#F5C500] block mb-4"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Nos Réalisations
            </span>
            <h2 
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1a1a1a] mb-6"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              {getSetting('home_gallery_title', 'Galerie de Nos Travaux')}
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {galleryImages.map((image, index) => (
              <div 
                key={index} 
                className="gallery-item aspect-square"
                onClick={() => openLightbox(index)}
                data-testid={`gallery-item-${index}`}
              >
                <img 
                  src={image.src} 
                  alt={image.alt}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#1a1a1a] py-20" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            {getSetting('home_cta_title', 'Besoin d\'une Intervention ?')}
          </h2>
          <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
            {getSetting('home_cta_subtitle', 'Contactez-nous dès maintenant pour un devis gratuit.')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary" data-testid="cta-devis">
              Demander un Devis
            </Link>
            <a href={`tel:${getSetting('phone', '0693526449')}`} className="btn-secondary flex items-center justify-center gap-2">
              <Phone size={20} />
              Appelez-nous
            </a>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxOpen && (
        <Lightbox
          images={galleryImages}
          currentIndex={currentImageIndex}
          onClose={() => setLightboxOpen(false)}
          onPrev={() => setCurrentImageIndex(Math.max(0, currentImageIndex - 1))}
          onNext={() => setCurrentImageIndex(Math.min(galleryImages.length - 1, currentImageIndex + 1))}
        />
      )}
    </div>
  );
};

export default Home;
